import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Alert } from 'src/domains/common/components/alert/alert';
import { Button } from 'src/domains/common/components/button/button';
import { Switch } from 'src/domains/common/components/switch/switch';
import Loading from 'src/components/Loading/Loading';
import { useAppStateContext } from 'src/AppState/AppState';
import { SourceOfFundsState } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.state';
import { SourceOfFundsThresholdPanel } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsThresholdPanel';
import { SourceOfFundsNewAccountPanel } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsNewAccountPanel';
import { SourceOfFundsTestTriggerPanel } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsTestTriggerPanel';
import { SourceOfFundsConfigAudit } from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFundsConfigAudit';
import {
    SourceOfFundsActions,
    SourceOfFundsDescription,
    SourceOfFundsSectionTitle,
    SourceOfFundsSwitchRow,
    SourceOfFundsWrapper,
} from 'src/components/ComplianceConfiguration/SourceOfFunds/SourceOfFunds.style';

export const SourceOfFunds = observer((): JSX.Element => {
    const appState = useAppStateContext();
    const [state] = useState(() => new SourceOfFundsState(appState));
    const [isAuditOpen, setIsAuditOpen] = useState(false);

    if (state.isLoading) {
        return (
            <SourceOfFundsWrapper data-test='source-of-funds-loading'>
                <Loading active />
            </SourceOfFundsWrapper>
        );
    }

    return (
        <SourceOfFundsWrapper data-test='source-of-funds'>
            <Alert
                icon='info'
                title='Configure automated deposit thresholds that trigger Source of Funds (SoF) compliance checks and define the automated actions executed upon breach.'
                variant='default'
            />

            {state.errorMessage === null ? null : (
                <Alert
                    icon='info'
                    title={state.errorMessage}
                    variant='destructive'
                />
            )}

            <SourceOfFundsSwitchRow data-test='source-of-funds-enable-row'>
                <SourceOfFundsSectionTitle>Enable Source of Funds</SourceOfFundsSectionTitle>
                <Switch
                    checked={state.thresholdEnabled}
                    dataTest='source-of-funds-threshold-enabled'
                    label={state.thresholdEnabled ? 'Yes' : 'No'}
                    onChange={(event): void => {
                        state.setThresholdEnabled(event.target.checked);
                    }}
                />
            </SourceOfFundsSwitchRow>

            {state.thresholdEnabled ? (
                <>
                    <SourceOfFundsDescription>
                        Two triggers are configured independently, each with its own automated action.
                    </SourceOfFundsDescription>

                    <SourceOfFundsThresholdPanel state={state} />
                    <SourceOfFundsNewAccountPanel state={state} />
                </>
            ) : null}

            <SourceOfFundsActions>
                <Button
                    dataTest='source-of-funds-save-configuration'
                    disabled={state.isSaving || state.hasValidationErrors}
                    isLoading={state.isSaving}
                    onClick={(): void => {
                        void state.saveConfiguration();
                    }}
                    type='button'
                >
                    Save configuration
                </Button>
            </SourceOfFundsActions>

            {state.thresholdEnabled ? <SourceOfFundsTestTriggerPanel state={state} /> : null}

            <SourceOfFundsActions>
                <Button
                    dataTest='source-of-funds-view-change-audit'
                    onClick={(): void => {
                        setIsAuditOpen(true);
                    }}
                    type='button'
                    variant='secondary'
                >
                    View change audit
                </Button>
            </SourceOfFundsActions>

            {isAuditOpen ? (
                <SourceOfFundsConfigAudit
                    onClose={(): void => {
                        setIsAuditOpen(false);
                    }}
                />
            ) : null}
        </SourceOfFundsWrapper>
    );
});
